import React, { useState, useEffect } from "react";
import { 
  Building, 
  BookOpen, 
  Clock, 
  Search, 
  ArrowLeft, 
  Check, 
  X, 
  AlertCircle,
  Award,
  ChevronRight,
  FileSpreadsheet,
  CheckCircle2,
  Users,
  Calendar
} from "lucide-react";
import { 
  fetchBranchList, 
  fetchBranchCourseList, 
  fetchCourseList, 
  fetchBatchList
} from "../../services/masterService";
import { getAttendanceStudents, submitAttendance } from "../../services/attendanceService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dummyLogo from "../../asset/avathar.jpg";
import "./Attendance.css";

const Attendance = () => {
  // Navigation Steps: 'branches' | 'courses' | 'batches-students'
  const [currentStep, setCurrentStep] = useState("branches");
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Data States
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [students, setStudents] = useState([]);
  const [studentAttendance, setStudentAttendance] = useState({});

  // Filter & Viewer States
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split("T")[0]);
  const [zoomedImage, setZoomedImage] = useState(null);

  // Success Confirmation Popup State
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successDetails, setSuccessDetails] = useState({ batchName: "", count: 0 });

  // Load Branches on mount
  useEffect(() => {
    loadBranches();
  }, []);

  // Fetch Attendance Roster when Batch or Date changes
  useEffect(() => {
    if (currentStep === "batches-students" && selectedBatch && attendanceDate) {
      loadAttendanceRoster(selectedBatch.BatchId || selectedBatch.id, attendanceDate);
    }
  }, [selectedBatch, attendanceDate, currentStep]);

  const loadAttendanceRoster = async (batchId, date) => {
    try {
      setIsLoading(true);
      const res = await getAttendanceStudents({ batchId: Number(batchId), attendanceDate: date });
      const studentData = res.data || [];
      setStudents(studentData);
      
      const initialAttendance = {};
      studentData.forEach(s => {
        const cid = s.courseDetailsId || s.CourseDetailsId;
        initialAttendance[cid] = s.isPresent || s.IsPresent || false;
      });
      setStudentAttendance(initialAttendance);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to retrieve attendance roster.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      const res = await fetchBranchList();
      setBranches(res.data || []);
    } catch (err) {
      console.error("Failed to load branches", err);
      setErrorMsg("Could not load branches. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1 -> Step 2: Select Branch and load Mapped Courses
  const handleSelectBranch = async (branch) => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      setSelectedBranch(branch);
      setSearchQuery("");

      // Fetch mapped courses and full course list concurrently
      const [bcRes] = await Promise.all([
        fetchBranchCourseList(),
        fetchCourseList()
      ]);

      const branchId = branch.BranchId || branch.id;
      // Find this branch's courses
      const branchData = (bcRes.data || []).find(item => String(item.BranchId) === String(branchId));

      if (branchData && branchData.CourseList) {
        setCourses(branchData.CourseList);
      } else {
        setCourses([]);
      }
      
      setCurrentStep("courses");
    } catch (err) {
      console.error("Failed to load course details", err);
      setErrorMsg("Failed to retrieve course details for this branch.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2 -> Step 3: Select Course and load Batches & Students
  const handleSelectCourse = async (course) => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      setSelectedCourse(course);
      setSearchQuery("");

      const branchCourseId = course.BranchCourseId;

      // Fetch batches
      const batchRes = await fetchBatchList();

      // 1. Process Batches
      // Filter batches by BranchCourseId
      const filteredBatches = (batchRes.data || []).filter(
        b => String(b.BranchCourseId) === String(branchCourseId)
      );
      setBatches(filteredBatches);
      
      // Auto-select first batch if available
      const defaultBatch = filteredBatches.length > 0 ? filteredBatches[0] : null;
      setSelectedBatch(defaultBatch);

      // (Students will be automatically fetched by the useEffect based on selectedBatch and attendanceDate)

      setCurrentStep("batches-students");
    } catch (err) {
      console.error("Failed to load batch details", err);
      setErrorMsg("Failed to retrieve batches for the selected course.");
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle present/absent for a student
  const handleToggleAttendance = (courseDetailsId, status) => {
    setStudentAttendance(prev => ({
      ...prev,
      [courseDetailsId]: status
    }));
  };

  // Bulk set attendance for all currently filtered students in batch
  const handleMarkAll = (status) => {
    const updated = { ...studentAttendance };
    filteredStudents.forEach(s => {
      const cid = s.courseDetailsId || s.CourseDetailsId;
      updated[cid] = status;
    });
    setStudentAttendance(updated);
  };

  // Switch Active Batch
  const handleSelectBatch = (batch) => {
    setSelectedBatch(batch);
  };

  // Filter students by search input (no need to filter by batch as API returns only for active batch)
  const getFilteredStudents = () => {
    if (!selectedBatch) return [];
    
    let batchStudents = students;

    // Apply search filter if query is present
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      batchStudents = batchStudents.filter(
        s => 
          (s.studentName && s.studentName.toLowerCase().includes(query)) ||
          (s.StudentName && s.StudentName.toLowerCase().includes(query)) ||
          (s.Name && s.Name.toLowerCase().includes(query)) ||
          (s.PhoneNumber && s.PhoneNumber.includes(query))
      );
    }

    return batchStudents;
  };

  const filteredStudents = getFilteredStudents();

  // Stats calculation
  const totalInBatch = filteredStudents.length;
  const presentCount = filteredStudents.filter(s => studentAttendance[s.courseDetailsId || s.CourseDetailsId] === true).length;
  const absentCount = totalInBatch - presentCount;
  const attendanceRate = totalInBatch > 0 ? Math.round((presentCount / totalInBatch) * 100) : 0;

  // Submit handler
  const handleSubmitAttendance = async () => {
    try {
      setIsLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      
      const payload = {
        batchId: Number(selectedBatch?.BatchId || selectedBatch?.id),
        date: attendanceDate,
        attendanceTakenBy: String(user.userId || user.id || ""),
        attendance: filteredStudents.map(s => {
          const cid = s.courseDetailsId || s.CourseDetailsId;
          return {
            courseDetailsId: cid,
            isPresent: studentAttendance[cid] || false
          };
        })
      };

      await submitAttendance(payload);

      setSuccessDetails({
        batchName: selectedBatch?.BatchName || "N/A",
        count: presentCount
      });
      setShowSuccessPopup(true);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to submit attendance.");
    } finally {
      setIsLoading(false);
    }
  };

  // Export Attendance report to Excel
  const handleExportExcel = () => {
    if (filteredStudents.length === 0) return;

    const exportData = filteredStudents.map((s, idx) => {
      const cid = s.courseDetailsId || s.CourseDetailsId;
      return {
        "S.No": idx + 1,
        "Student ID": s.studentId || s.StudentId || "N/A",
        "Student Name": s.studentName || s.StudentName || s.Name || "N/A",
        "Status": studentAttendance[cid] ? "Present" : "Absent",
        "Date": attendanceDate,
        "Branch": selectedBranch?.BranchName || selectedBranch?.branchName || "N/A",
        "Course": selectedCourse?.CourseName || selectedCourse?.courseName || "N/A",
        "Batch": selectedBatch?.BatchName || selectedBatch?.batchName || "N/A"
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Roster");

    // Auto-fit column widths
    const maxLens = {};
    exportData.forEach(row => {
      Object.keys(row).forEach(key => {
        const val = String(row[key]);
        maxLens[key] = Math.max(maxLens[key] || 10, val.length + 2);
      });
    });
    worksheet["!cols"] = Object.keys(maxLens).map(key => ({ wch: maxLens[key] }));

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
    
    const branchName = selectedBranch?.BranchName || selectedBranch?.branchName || "Branch";
    const batchName = selectedBatch?.BatchName || selectedBatch?.batchName || "Batch";
    
    saveAs(
      fileData, 
      `Attendance_${branchName.replace(/\s+/g, "_")}_${batchName.replace(/\s+/g, "_")}_${attendanceDate}.xlsx`
    );
  };

  // Reset to previous steps
  const handleGoBack = () => {
    if (currentStep === "courses") {
      setCurrentStep("branches");
      setSelectedBranch(null);
      setCourses([]);
    } else if (currentStep === "batches-students") {
      setCurrentStep("courses");
      setSelectedCourse(null);
      setBatches([]);
      setSelectedBatch(null);
      setStudents([]);
    }
    setSearchQuery("");
    setErrorMsg("");
  };

  return (
    <div className="attendance-explorer">
      {/* 🚀 Top Breadcrumbs Navigator & Back Link */}
      <div className="explorer-header">
        <div className="header-left-area">
          {currentStep !== "branches" && (
            <button className="back-nav-btn" onClick={handleGoBack}>
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>
          )}
          <div className="breadcrumbs-wrap">
            <span className={`crumb ${currentStep === "branches" ? "active" : ""}`} onClick={() => { if (currentStep !== "branches") { setCurrentStep("branches"); setSelectedBranch(null); setCourses([]); } }}>
              Branches
            </span>
            {selectedBranch && (
              <>
                <ChevronRight size={14} className="crumb-separator" />
                <span className={`crumb ${currentStep === "courses" ? "active" : ""}`} onClick={() => { if (currentStep === "batches-students") { setCurrentStep("courses"); setSelectedCourse(null); setBatches([]); } }}>
                  {selectedBranch.BranchName}
                </span>
              </>
            )}
            {selectedCourse && (
              <>
                <ChevronRight size={14} className="crumb-separator" />
                <span className="crumb active">
                  {selectedCourse.CourseName}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Global Search Bar (Only shown at list steps) */}
        {currentStep !== "batches-students" && (
          <div className="explorer-search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder={
                currentStep === "branches" 
                  ? "Search branches by name, city..." 
                  : "Search courses by name..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>

      {errorMsg && (
        <div className="explorer-error-alert">
          <AlertCircle size={20} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="explorer-loading-state">
          <div className="spinner"></div>
          <p>Loading details, please wait...</p>
        </div>
      )}

      {!isLoading && (
        <div className="explorer-main-content">
          {/* ======================================================
              STEP 1: BRANCHES VIEW
             ====================================================== */}
          {currentStep === "branches" && (
            <div className="step-container branches-step animate-fade-in">
              <div className="step-intro">
                <h2>Branch Directory</h2>
                <p>Choose a branch office from below to view mapped academic courses and record student attendance.</p>
              </div>

              <div className="card-grid branches-grid">
                {branches
                  .filter(b => 
                    b.BranchName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    b.City?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(branch => (
                    <div 
                      key={branch.BranchId || branch.id} 
                      className="premium-explorer-card branch-card"
                      onClick={() => handleSelectBranch(branch)}
                    >
                      <div className="card-top-accent"></div>
                      <div className="card-icon-wrapper">
                        <Building size={24} />
                      </div>
                      <div className="card-body">
                        <h3>{branch.BranchName}</h3>
                        <span className="code-badge">{branch.BranchCode || "BR-MAIN"}</span>
                        
                        <div className="branch-meta">
                          <p><strong>Contact:</strong> {branch.ContactName || "N/A"}</p>
                          <p><strong>Phone:</strong> {branch.ContactPhone || "N/A"}</p>
                          <p><strong>City:</strong> {branch.City || "N/A"}</p>
                        </div>
                      </div>
                      <div className="card-footer">
                        <button className="card-action-btn">
                          <span>Course Details</span>
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  ))}

                {branches.length === 0 && (
                  <div className="empty-explorer-state">
                    <Building size={48} />
                    <h3>No Branches Found</h3>
                    <p>There are currently no active branch records in the system configuration.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ======================================================
              STEP 2: COURSES VIEW (SIMPLIFIED AS REQUESTED)
             ====================================================== */}
          {currentStep === "courses" && (
            <div className="step-container courses-step animate-fade-in">
              <div className="step-intro">
                <h2>Courses in {selectedBranch?.BranchName}</h2>
                <p>Select a course below to drill down into scheduled batches and students roster.</p>
              </div>

              <div className="card-grid courses-grid">
                {courses
                  .filter(c => c.CourseName?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(course => (
                    <div 
                      key={course.CourseId} 
                      className="premium-explorer-card course-card simplified"
                      onClick={() => handleSelectCourse(course)}
                    >
                      <div className="card-top-accent course-accent"></div>
                      <div className="card-icon-wrapper course-icon">
                        <BookOpen size={24} />
                      </div>
                      <div className="card-body">
                        <h3>{course.CourseName}</h3>
                      </div>
                      <div className="card-footer">
                        <button className="card-action-btn course-action">
                          <span>Select Students</span>
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  ))}

                {courses.length === 0 && (
                  <div className="empty-explorer-state">
                    <BookOpen size={48} />
                    <h3>No Courses Mapped</h3>
                    <p>There are no courses mapped to {selectedBranch?.BranchName} yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ======================================================
              STEP 3: BATCHES & STUDENTS VIEW (FULL WIDTH LAYOUT)
             ====================================================== */}
          {currentStep === "batches-students" && (
            <div className="step-container roster-step full-width animate-fade-in">
              {/* Roster Main - Occupies Full Screen width */}
              <div className="roster-main full-width">
                {/* Active Batch Summary & Filters (Always Rendered at Top) */}
                <div className="roster-top-panel">
                  <div className="batch-info-block">
                    <h2>Batch Attendance</h2>
                  </div>
                  
                  {/* Search, Date & Batch Filters Row */}
                  <div className="roster-filter-actions">
                    {/* Batch Dropdown Selector */}
                    <div className="filter-select-group">
                      <Clock size={16} className="filter-icon" />
                      <select
                        value={selectedBatch?.BatchId || ""}
                        onChange={(e) => {
                          const batchId = e.target.value;
                          const found = batches.find(b => String(b.BatchId) === String(batchId));
                          if (found) handleSelectBatch(found);
                        }}
                        className="attendance-batch-select"
                        title="Select Batch"
                      >
                        <option value="">-- Choose Batch --</option>
                        {batches.map(batch => (
                          <option key={batch.BatchId} value={batch.BatchId}>
                            {batch.BatchName} ({batch.BatchTime || "All Day"})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Date Filter */}
                    <div className="date-picker-group">
                      <Calendar size={16} className="date-icon" />
                      <input 
                        type="date" 
                        value={attendanceDate}
                        onChange={(e) => setAttendanceDate(e.target.value)}
                        className="attendance-date-picker"
                        title="Attendance Date"
                      />
                    </div>

                    {/* Search Input */}
                    <div className="roster-search-bar">
                      <Search size={16} className="search-icon" />
                      <input 
                        type="text" 
                        placeholder="Filter student list..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={!selectedBatch}
                      />
                    </div>
                  </div>
                </div>

                {selectedBatch ? (
                  <div className="roster-inner">

                    {/* Attendance Quick Analytics Card */}
                    <div className="attendance-analytics-row">
                      <div className="analytic-card">
                        <div className="analytic-icon icon-users"><Users size={20} /></div>
                        <div className="analytic-details">
                          <span className="analytic-lbl">Total Students</span>
                          <span className="analytic-val">{totalInBatch}</span>
                        </div>
                      </div>
                      <div className="analytic-card card-present">
                        <div className="analytic-icon icon-present"><Check size={20} /></div>
                        <div className="analytic-details">
                          <span className="analytic-lbl">Present</span>
                          <span className="analytic-val">{presentCount}</span>
                        </div>
                      </div>
                      <div className="analytic-card card-absent">
                        <div className="analytic-icon icon-absent"><X size={20} /></div>
                        <div className="analytic-details">
                          <span className="analytic-lbl">Absent</span>
                          <span className="analytic-val">{absentCount}</span>
                        </div>
                      </div>
                      <div className="analytic-card card-percentage">
                        <div className="analytic-icon icon-percentage"><Award size={20} /></div>
                        <div className="analytic-details">
                          <span className="analytic-lbl">Attendance Rate</span>
                          <span className="analytic-val">{attendanceRate}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Student Table Card (Parents column removed, Picture Zoom and Bulk Actions added) */}
                    <div className="roster-table-card">
                      <div className="table-header-toolbar">
                        <h3>Student Roll Call</h3>
                        
                        <div className="toolbar-right-btns">
                          {filteredStudents.length > 0 && (
                            <button type="button" className="export-excel-btn" onClick={handleExportExcel}>
                              <FileSpreadsheet size={16} />
                              <span>Export Roster</span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="table-scroll-area">
                        <table className="roster-table">
                          <thead>
                            <tr>
                              <th className="text-center" style={{ width: "80px" }}>Roll No.</th>
                              <th style={{ width: "90px" }} className="text-center">Photo</th>
                              <th>Student Details</th>
                              <th className="text-center" style={{ width: "260px" }}>
                                <div className="header-bulk-mark">
                                  <span>Attendance State</span>
                                  <div className="bulk-toggle-pill">
                                    <button 
                                      className="bulk-pill-btn present" 
                                      onClick={() => handleMarkAll(true)}
                                      title="Mark all Present"
                                    >
                                      All P
                                    </button>
                                    <button 
                                      className="bulk-pill-btn absent" 
                                      onClick={() => handleMarkAll(false)}
                                      title="Mark all Absent"
                                    >
                                      All A
                                    </button>
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredStudents.map((student, idx) => {
                              const cid = student.courseDetailsId || student.CourseDetailsId;
                              const status = studentAttendance[cid] !== undefined ? studentAttendance[cid] : false;
                              const imageUrl = student.StudentProfilePicUrl || student.ProfilePic || student.Photo || dummyLogo;

                              return (
                                  <tr key={cid || idx}>
                                    <td className="text-center">
                                      <span className="roll-number">{idx + 1}</span>
                                    </td>
                                    <td className="text-center">
                                      <div className="roster-photo-container">
                                        <img 
                                          src={imageUrl} 
                                          alt={student.studentName || student.StudentName || student.Name} 
                                          className="roster-avatar-img clickable-avatar"
                                          onClick={() => setZoomedImage(imageUrl)}
                                          onError={(e) => { e.currentTarget.src = dummyLogo; }}
                                        />
                                      </div>
                                    </td>
                                    <td>
                                      <div className="roster-student-cell-simple">
                                        <span className="student-name">{student.studentName || student.StudentName || student.Name}</span>
                                        <span className="student-id-txt">ID: {student.studentId || student.StudentId}</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="attendance-toggle-wrapper">
                                        <div className="compact-sleek-toggle">
                                          <button 
                                            className={`sleek-status-btn present ${status === true ? "active" : ""}`}
                                            onClick={() => handleToggleAttendance(cid, true)}
                                            title="Mark Present"
                                          >
                                            <Check size={14} />
                                          </button>
                                          <button 
                                            className={`sleek-status-btn absent ${status === false ? "active" : ""}`}
                                            onClick={() => handleToggleAttendance(cid, false)}
                                            title="Mark Absent"
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                            })}

                            {filteredStudents.length === 0 && (
                              <tr>
                                <td colSpan="4" className="empty-table-cell">
                                  <div className="table-empty-wrap">
                                    <Users size={32} />
                                    <p>No student records mapped to this batch roster.</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Submit Action Box */}
                      {filteredStudents.length > 0 && (
                        <div className="roster-action-footer">
                          <button className="submit-attendance-btn" onClick={handleSubmitAttendance}>
                            <CheckCircle2 size={18} style={{ marginRight: 8 }} />
                            <span>Submit Attendance Roster</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="empty-roster-placeholder">
                    <Clock size={48} />
                    <h3>No Batch Selected</h3>
                    <p>Select a scheduled class batch from the top panel to populate the student roll call roster.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox / Zoom Profile Picture Modal */}
      {zoomedImage && (
        <div className="lightbox-overlay" onClick={() => setZoomedImage(null)}>
          <div className="lightbox-card animate-zoom-in" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close-btn" onClick={() => setZoomedImage(null)}>
              <X size={20} />
            </button>
            <div className="lightbox-img-frame">
              <img src={zoomedImage} alt="Profile Large Zoom" onError={(e) => { e.currentTarget.src = dummyLogo; }} />
            </div>
          </div>
        </div>
      )}

      {/* Success Notification Popup */}
      {showSuccessPopup && (
        <div className="explorer-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
          <div className="explorer-popup-card" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon-badge">
              <Check size={36} />
            </div>
            <h2>Attendance Logged!</h2>
            <p>
              The attendance roster for batch <strong>{successDetails.batchName}</strong> on <strong>{attendanceDate}</strong> has been saved.
            </p>
            <div className="popup-metric">
              <span className="metric-lbl">Students Present:</span>
              <span className="metric-val">{successDetails.count}</span>
            </div>
            <button className="popup-close-btn" onClick={() => setShowSuccessPopup(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
